// No production defaults belong in the browser bundle. Build inputs are explicit.
export function validateEnvironment(raw) {
  const {
    environment,
    firebase,
    functionsOrigin,
    appBaseUrl,
    databaseId,
    allowedOrigins,
  } = raw;
  if (!["production", "staging", "local"].includes(environment))
    throw new Error("Explicit ManagerLogix environment required.");
  for (const key of [
    "apiKey",
    "projectId",
    "authDomain",
    "storageBucket",
    "appId",
  ]) {
    if (!firebase?.[key]) throw new Error(`Missing Firebase ${key}`);
  }
  if (
    !databaseId ||
    !functionsOrigin ||
    !appBaseUrl ||
    !Array.isArray(allowedOrigins) ||
    !allowedOrigins.length
  ) {
    throw new Error("Incomplete environment configuration.");
  }
  const local = environment === "local";
  const origin = new URL(appBaseUrl).origin;
  if (!allowedOrigins.includes(origin))
    throw new Error("Application origin must be explicitly allowed.");
  if (environment !== "production") {
    if (
      firebase.apiKey.startsWith("AIzaSyCI1pC6ka") ||
      String(firebase.appId).includes("111185480919")
    )
      throw new Error(
        "Production Auth credentials are forbidden in an isolated build.",
      );
    if (databaseId !== "(default)")
      throw new Error(
        "Isolated API currently requires the project's default database.",
      );
    const prefix = local ? /^demo-managerlogix-/ : /^managerlogix-staging-/;
    if (!prefix.test(firebase.projectId))
      throw new Error("Non-production project name required.");
    if (JSON.stringify(raw).includes("propertymanagerapp"))
      throw new Error("Production reference forbidden outside production.");
    if (
      firebase.authDomain !== `${firebase.projectId}.firebaseapp.com` ||
      ![
        `${firebase.projectId}.firebasestorage.app`,
        `${firebase.projectId}.appspot.com`,
      ].includes(firebase.storageBucket)
    ) {
      throw new Error(
        "Firebase resources must belong to the selected project.",
      );
    }
    if (
      !local &&
      (functionsOrigin !==
        `https://us-central1-${firebase.projectId}.cloudfunctions.net` ||
        origin !== `https://${firebase.projectId}.web.app` ||
        allowedOrigins.some((v) => v !== origin))
    ) {
      throw new Error("Staging origins must belong exclusively to staging.");
    }
    if (
      local &&
      ![functionsOrigin, appBaseUrl].every((v) =>
        /^http:\/\/127\.0\.0\.1:\d+/.test(v),
      )
    ) {
      throw new Error("Local services must be loopback emulators.");
    }
  }
  return Object.freeze({ ...raw, firebase: Object.freeze({ ...firebase }) });
}
export function readEnvironment() {
  if (
    process.env.NODE_ENV === "test" &&
    !process.env.REACT_APP_MANAGERLOGIX_ENV
  ) {
    return validateEnvironment({
      environment: "local",
      firebase: {
        projectId: "demo-managerlogix-test",
        apiKey: "synthetic",
        appId: "synthetic",
        authDomain: "demo-managerlogix-test.firebaseapp.com",
        storageBucket: "demo-managerlogix-test.firebasestorage.app",
      },
      functionsOrigin:
        "http://127.0.0.1:5001/demo-managerlogix-test/us-central1",
      appBaseUrl: "http://127.0.0.1:3000",
      databaseId: "(default)",
      allowedOrigins: ["http://127.0.0.1:3000"],
    });
  }
  return validateEnvironment({
    environment: process.env.REACT_APP_MANAGERLOGIX_ENV,
    firebase: JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG || "null"),
    functionsOrigin: process.env.REACT_APP_FUNCTIONS_ORIGIN,
    appBaseUrl: process.env.REACT_APP_APP_BASE_URL,
    databaseId: process.env.REACT_APP_FIRESTORE_DATABASE,
    allowedOrigins: JSON.parse(process.env.REACT_APP_ALLOWED_ORIGINS || "null"),
  });
}
export const appEnvironment = readEnvironment();
export function assertSafeResourceUrl(value, configuration = appEnvironment) {
  const url = new URL(String(value), configuration.appBaseUrl);
  if (configuration.environment !== "production") {
    const decoded = decodeURIComponent(url.href);
    if (
      /propertymanagerapp/i.test(decoded) ||
      /(^|\.)managerlogix\.com$/i.test(url.hostname)
    )
      throw new Error("Production request blocked in isolated environment.");
    const project = decoded.match(/\/projects\/([^/]+)/)?.[1];
    if (project && project !== configuration.firebase.projectId)
      throw new Error("Cross-project request blocked.");
    if (
      url.searchParams.has("key") &&
      url.searchParams.get("key") !== configuration.firebase.apiKey
    )
      throw new Error("Cross-project Auth key blocked.");
  }
  return url.href;
}
export function installEnvironmentNetworkGuard(configuration = appEnvironment) {
  if (
    configuration.environment === "production" ||
    typeof window === "undefined"
  )
    return;
  if (
    process.env.NODE_ENV !== "test" &&
    !configuration.allowedOrigins.includes(window.location.origin)
  )
    throw new Error("Browser origin does not match the isolated environment.");
  if (window.fetch) {
    const fetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
      assertSafeResourceUrl(input?.url || input, configuration);
      return fetch(input, init);
    };
  }
  const original = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    assertSafeResourceUrl(url, configuration);
    return original.call(this, method, url, ...rest);
  };
  if (window.WebSocket) {
    const OriginalWebSocket = window.WebSocket;
    window.WebSocket = class extends OriginalWebSocket {
      constructor(url, ...args) {
        assertSafeResourceUrl(url, configuration);
        super(url, ...args);
      }
    };
  }
}
